'use client';

import { Box, useMediaQuery } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useState } from 'react';

export default function FAQ() {
  const t = useTranslations('klarna.faq');
  const isMobile = useMediaQuery('(max-width:700px)');
  const [expandedItem, setExpandedItem] = useState<number | false>(false);

  // FAQ数据
  const faqList = [
    {
      key: 'whatHappensIfIMakeAReturn',
      title: t('whatHappensIfIMakeAReturn.title'),
      content: t('whatHappensIfIMakeAReturn.content'),
    },
    {
      key: 'theressSomethingWrongWithMyOrder',
      title: t('theressSomethingWrongWithMyOrder.title'),
      content: t('theressSomethingWrongWithMyOrder.content'),
    },
    {
      key: 'iHaveNotReceivedMyOrder',
      title: t('iHaveNotReceivedMyOrder.title'),
      content: t('iHaveNotReceivedMyOrder.content'),
    },
    {
      key: 'myStatementIsIncorrect',
      title: t('myStatementIsIncorrect.title'),
      content: t('myStatementIsIncorrect.content'),
    },
  ];

  // 处理展开/收起
  const handleChange = (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedItem(isExpanded ? index : false);
  };

  // 检查是否展开
  const isExpanded = (index: number) => expandedItem === index;

  return (
    <Box className={isMobile ? 'mt-1' : 'mt-[30px]'}>
      <Box component="ul" className="space-y-0">
        {faqList.map((item, index) => (
          <Box
            component="li"
            key={index}
            className={isMobile ? 'mt-[10px] overflow-hidden rounded-md' : 'mt-[2px]'}
          >
            <Accordion
              expanded={isExpanded(index)}
              onChange={handleChange(index)}
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                border: 'none',
                margin: 0,
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
                // 移动端样式
                ...(isMobile && {
                  borderRadius: '8px',
                  overflow: 'hidden',
                }),
              }}
            >
              <AccordionSummary
                expandIcon={
                  <CollapseArrowDownOutlined className={isMobile ? 'text-[16px]' : 'text-[16px]'} />
                }
                sx={{
                  // 移动端样式
                  ...(isMobile && {
                    padding: '19px 10px',
                    minHeight: 'auto',
                    backgroundColor: isExpanded(index) ? '#E7E7E7' : '#F5F5F5',
                    '& .MuiAccordionSummary-content': {
                      margin: '0 !important',
                    },
                    '&.Mui-expanded': {
                      minHeight: 'auto',
                      margin: 0,
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      transition: 'transform 0.3s ease',
                      transformOrigin: 'center',
                    },
                    '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                      transform: 'rotate(180deg)',
                    },
                  }),
                  // PC端样式
                  ...(!isMobile && {
                    backgroundColor: isExpanded(index) ? '#E7E7E7' : '#F5F6F7',
                    fontFamily: isExpanded(index)
                      ? 'var(--font-bricolage-bold)'
                      : 'var(--font-bricolage)',
                    fontSize: '18px',
                    height: '60px',
                    paddingLeft: '30px',
                    minHeight: 'auto',
                    '& .MuiAccordionSummary-content': {
                      margin: '0 !important',
                    },
                    '&.Mui-expanded': {
                      minHeight: 'auto',
                      margin: 0,
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      transition: 'transform 0.3s ease',
                      transformOrigin: 'center',
                    },
                    '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                      transform: 'rotate(180deg)',
                    },
                  }),
                }}
              >
                <div
                  className={
                    isMobile
                      ? 'text-[14px] leading-[18px] flex-1'
                      : `${isExpanded(index) ? 'font-bb' : 'font-br'}`
                  }
                  dangerouslySetInnerHTML={{
                    __html: isMobile ? item.title.replace('.', '.<br/>') : item.title,
                  }}
                />
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  // 移动端样式
                  ...(isMobile && {
                    padding: '15px 10px',
                    backgroundColor: '#F5F5F5',
                    margin: 0,
                  }),
                  // PC端样式
                  ...(!isMobile && {
                    padding: '0 50px 0 30px',
                    backgroundColor: '#F5F6F7',
                    fontFamily: 'var(--font-instrument)',
                    '& > div': {
                      margin: 0,
                      padding: '24px 0',
                      fontSize: '16px',
                    },
                  }),
                }}
              >
                <div className={isMobile ? 'text-[14px] leading-[21px] font-ir' : ''}>
                  {item.content}
                </div>
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
